import * as React from "react";
import { CornerDownLeft, MessageSquarePlus, Sparkles, X } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { useChat } from "@/lib/ChatContext";
import { ChatMessageView } from "./ChatMessage";
import { Empty } from "./ui/empty";

export function RightRailChat() {
  const { state, send, streaming, cancel, newConversation, setOpen, pendingConfirmation, confirm } =
    useChat();
  const [input, setInput] = React.useState("");
  const taRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // autoscroll on new tokens
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [state.messages, streaming]);

  // focus textarea when panel opens / new conversation
  React.useEffect(() => {
    taRef.current?.focus();
  }, [state.session_id]);

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const t = input.trim();
    if (!t || streaming) return;
    setInput("");
    await send(t);
  };

  const onKey: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-11 items-center gap-2 border-b border-border px-3">
        <Sparkles className="h-3.5 w-3.5 text-brand" />
        <div className="text-sm font-medium">Kitt</div>
        <div className="text-2xs text-muted-foreground">/ copilot</div>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="iconSm"
          onClick={newConversation}
          title="New conversation"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="iconSm"
          onClick={() => setOpen(false)}
          title="Hide (cmd-k)"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </header>

      <ScrollArea className="flex-1">
        <div ref={scrollRef} className="flex flex-col gap-3 px-3 py-4">
          {state.messages.length === 0 ? (
            <div className="px-1 py-2">
              <Empty
                title="Hi Justin"
                hint={
                  <>
                    Ask about the queue, a creator's week, or have me pause/unpause someone.
                    I'll always describe before I do.
                  </>
                }
              />
              <div className="mt-3 grid gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="rounded-md border border-border bg-card/40 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            state.messages.map((m, i) => <ChatMessageView key={i} message={m} />)
          )}
          {pendingConfirmation ? (
            <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs">
              <div className="mb-2 font-medium text-warning">{pendingConfirmation.prompt}</div>
              <div className="flex gap-2">
                <Button size="xs" onClick={() => confirm(true)}>
                  Yes, do it
                </Button>
                <Button size="xs" variant="outline" onClick={() => confirm(false)}>
                  No, hold off
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </ScrollArea>

      <form
        onSubmit={onSubmit}
        className="border-t border-border bg-card/60 p-2"
      >
        <div className="flex items-end gap-2 rounded-md border border-input bg-background/40 p-1.5 focus-within:border-ring/70">
          <Textarea
            ref={taRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask Kitt anything…"
            disabled={streaming}
            rows={2}
            className="max-h-40 min-h-0 resize-none border-0 bg-transparent px-1 py-1 text-sm focus-visible:border-0 focus-visible:bg-transparent"
          />
          <div className="flex flex-col items-end gap-1">
            {streaming ? (
              <Button size="xs" variant="outline" onClick={cancel} type="button">
                stop
              </Button>
            ) : (
              <Button size="xs" type="submit" disabled={!input.trim()}>
                Send <CornerDownLeft className="ml-1 h-3 w-3" />
              </Button>
            )}
            <span className="text-[10px] text-muted-foreground">
              <span className="kbd">⏎</span> send · <span className="kbd">⇧⏎</span> nl
            </span>
          </div>
        </div>
      </form>
    </div>
  );
}

const SUGGESTIONS = [
  "What's in my triage right now?",
  "How did Erik do this week?",
  "Show me drafts older than an hour.",
  "Pause Mia for 24 hours — she's traveling.",
];
